'use client'

import { useEffect } from 'react'

/**
 * Global error boundary.
 * This catches errors that occur outside of nested error boundaries,
 * including chunk loading errors and other unhandled exceptions.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    // Check if this is a chunk loading error
    const isChunkError =
      error?.name === 'ChunkLoadError' ||
      error?.message?.includes('Loading chunk') ||
      error?.message?.includes('Failed to load chunk') ||
      error?.message?.includes('Loading CSS chunk')

    // Check if we've already attempted a reload
    const reloadAttempted = sessionStorage.getItem('chunk_reload_attempted')

    if (isChunkError && !reloadAttempted) {
      sessionStorage.setItem('chunk_reload_attempted', 'true')
      console.warn('[GlobalError] Chunk loading error detected, reloading...', error)
      window.location.reload()
      return
    }

    // Log to Datadog
    import('@datadog/browser-rum')
      .then(({ datadogRum }) => {
        datadogRum.addError(error, {
          errorType: isChunkError ? 'ChunkLoadError' : 'GlobalError',
          digest: error.digest,
        })
      })
      .catch(() => {
        // Datadog not available, fall back to console only
      })

    console.error('[GlobalError]', error)
  }, [error])

  const handleReload = () => {
    sessionStorage.removeItem('chunk_reload_attempted')
    window.location.reload()
  }

  return (
    <html lang="en">
      <body>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            padding: '20px',
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            backgroundColor: '#f8f9fa',
            color: '#333',
          }}
        >
          <div
            style={{
              maxWidth: '500px',
              textAlign: 'center',
              backgroundColor: 'white',
              padding: '40px',
              borderRadius: '8px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            }}
          >
            <h1
              style={{
                fontSize: '24px',
                marginBottom: '16px',
                color: '#333',
              }}
            >
              Something went wrong
            </h1>
            <p
              style={{
                fontSize: '16px',
                color: '#666',
                marginBottom: '24px',
                lineHeight: '1.5',
              }}
            >
              We encountered an unexpected error. This might be due to a recent update. Please try
              refreshing the page.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={handleReload}
                style={{
                  padding: '12px 24px',
                  fontSize: '16px',
                  backgroundColor: '#FF5A00',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: '500',
                }}
              >
                Refresh page
              </button>
              <button
                onClick={() => reset()}
                style={{
                  padding: '12px 24px',
                  fontSize: '16px',
                  backgroundColor: 'white',
                  color: '#333',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: '500',
                }}
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      </body>
    </html>
  )
}
